import { Editor } from '@ckeditor/ckeditor5-core/src/editor';
import { PDFExport } from '@progress/kendo-react-pdf';
import { Popover } from 'antd';
import classNames from 'classnames';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import TextSelectionPopover from 'react-text-selection-popover';
import { Document as Doc } from 'api/document/types/document';
import { ProjectId, UserType } from 'api/models';
import { ErrorBoundary } from 'components/molecules/error-boundary';
import { EditorsObject } from 'components/organisms/ck-collab-editor/custom-editor.types';
import { EditorModes } from 'components/organisms/ck-collab-editor/editor-enum';
import { PrivateSectionContext } from 'components/organisms/ck-collab-editor/private-section-context';
import { useContentClick } from 'components/organisms/document/document-hooks/useContentClick';
import { useTextSelection } from 'components/organisms/document/document-hooks/useTextSelection';
import { UrlParamTypes } from 'components/pages/project-details/project-spaces/project-spaces.types';
import { useSpaceActions } from 'components/pages/project-details/project-spaces/use-space-actions';
import { PermissionRuleName, EDITOR_LOCK_KEY } from 'constant';
import { useApprovalsPaneContext } from 'contexts/approvals-pane';
import {
  getDocTasks,
  getDocuments,
  setActiveDocument,
  updateActiveTask,
} from 'data/documents';
import {
  DocumentTabsFreeMode,
  selectDocumentTabsFreeMode,
  toggleDocumentTabsFreeMode,
} from 'data/layout';
import { useProjectDetail } from 'data/projects';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useCustomerPreview } from 'hooks/use-customer-preview';
import { useDistractionFreeMode } from 'hooks/use-distraction-free-mode';
import { useMentionsData } from 'hooks/use-mentions-data';
import { useOnUserMention } from 'hooks/use-on-user-mention';
import { useToggleFreeMode } from 'hooks/use-toggle-free-mode';
import { useUser } from 'hooks/use-user';
import { useUserType } from 'hooks/use-user-type';
import { queueMacrotask } from 'utils/macrotask';
import { INTERMEDIATE_COMMENT } from 'vendor/ckeditor/plugins/comment/insert-comment-command';
import { CKMentionPopup } from 'vendor/ckeditor/react-components/mention';
import { CKVariablesPopup } from 'vendor/ckeditor/react-components/variables';
import {
  PopupType,
  saveAllEditors,
  setActiveEditor,
  setBalloonToolbarDetails,
  setDocumentDetails,
  updateActiveTab,
  useDocuments,
} from './document-store';
import styles from './document.module.css';
import { FreezeAction, Props, TabNames } from './document.types';
import { LinkForm } from './parts/add-link-form';
import { CollabActions } from './parts/collab-actions';
import { DistractionFreeModeBtnStyled } from './parts/distraction-free-mode';
import {
  DocBalloonToolbar,
  DocBalloonToolbarRef,
  SupportedBalloonToolbarOptions,
} from './parts/doc-balloon-toolbar';
import {
  DocumentHeaderWrapper,
  HeaderWrapper,
  SpaceNameStyle,
} from './parts/document-head/styles';
import { MemoizedDocTabs } from './parts/document-tabs';
import { DocumentTaskPopup } from './parts/document-task-popup/document-task-popup';
import { DocumentEditor } from './parts/editor';
import { fontStyles } from './parts/fonts';
import { LastUpdatedBy } from './parts/last-updated-by';
import { renderFallback } from './parts/other/fallback-ui';
import { useForceUpdate } from './parts/other/use-force-update';
import { Topbar } from './parts/topbar';
import { CommentService, ContentCommentIdMap } from './services/comment';
import { TaskService, ToolbarPositions } from './services/task';

const toggleReadOnlyMode = (isReadOnly, editor) => {
  if (!editor) {
    return;
  }
  if (isReadOnly) {
    editor.enableReadOnlyMode(EDITOR_LOCK_KEY);
  } else {
    editor.disableReadOnlyMode(EDITOR_LOCK_KEY);
  }
};

export const Document: FC<Props> = (props) => {
  const {
    documentId,
    projectId,
    canShowComments = true,
    wrapperClassName,
    topbarClassName,
    editorClassName,
    className,
    onDocumentTitleChange,
    renderExtraToolbarOptions,
    isPrivate = false,
    isResponsive,
  } = props;

  const dispatch = useDispatch();
  const [state, documentStoreDispatch] = useDocuments();
  const { documentObj } = state;

  const [isReadyToExportPdf, setDocPdfReady] = useState<boolean>(false);
  const mentionsData = useMentionsData(ProjectId(projectId));
  const onUserMention = useOnUserMention(ProjectId(projectId));

  const { isPreview } = useCustomerPreview();

  useEffect(() => {
    dispatch(
      setActiveDocument({
        documentId,
        projectId,
      }),
    );
  }, [dispatch, documentId, projectId]);

  useEffect(() => {
    dispatch(getDocTasks({ documentId, projectId }));
    dispatch(getDocuments({ projectId }));
  }, [dispatch, documentId, projectId]);

  const refDocumentWrapper = useRef<HTMLDivElement | null>(null);
  const refBalloonPopup = useRef<HTMLDivElement | null>(null);
  const refMentionPopup = useRef<HTMLDivElement | null>(null);
  const refCollaboratorsListElement = useRef<HTMLDivElement | null>(null);
  const [showDiscussionForm, setDiscussionFormVisibility] = useState(false);
  const [showLinkFormData, setLinkFormData] = useState({
    showEditForm: false,
    href: '',
  });

  const [showTaskForm, setTaskForm] = useState(false);
  const [documentTitle, setDocTitle] = useState('');
  const [taskFormPosition, setTaskFormPosition] = useState({ top: 0, left: 0 });
  const [mentionPopupPosition, setMentionPopupPosition] = useState({
    top: 0,
    left: 0,
  });
  const [isDocTitleVisible, setDocTitleVisibility] = useState(true);
  // TODO: Implement fullscreen
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isDocFullScreen, setDocFullScreen] = useState(true);

  const [commentThreadId, setThreadId] = useState('');
  const [editorCommentMap, setEditorCommentMap] = useState({});
  const [newCommentBaseContent, setNewCommentBaseContent] = useState('');
  const [selection, setSelection] = useState<string | undefined>();
  const forceUpdate = useForceUpdate();

  const userType = useUserType();
  const isUserNative = userType === UserType.Native;
  const [privateSectionEnabled, setPrivateSectionStatus] =
    useState(isUserNative);

  const [docTitle, setTitle] = useState<string>('');

  const onTitleChange = (title: string) => {
    setTitle(title);
    if (onDocumentTitleChange) {
      onDocumentTitleChange(title);
    }
  };

  const docRef = useRef<HTMLInputElement | null>(null);
  const docContentWrapperRef = useRef<HTMLDivElement | null>(null);

  const documentsTabsActive = useSelector(selectDocumentTabsFreeMode);
  const onToggle = (value: boolean) => {
    dispatch(toggleDocumentTabsFreeMode(value));
  };
  const documentTabsFreeHandler = useToggleFreeMode({
    active: documentsTabsActive,
    localStorageKey: DocumentTabsFreeMode,
    onToggle,
  });

  const onCommentClickCallback = useCallback(
    (id) => {
      updateActiveTab(documentStoreDispatch, TabNames.CommentsTab);
      setThreadId(id);
      documentTabsFreeHandler.exit();
    },
    [documentStoreDispatch, documentTabsFreeHandler],
  );

  // We need selection to position the new comment form widget on the sidebar.
  const handleSelection = () => {
    // TODO: Support keyboard selection.
    const selctn = window.getSelection();

    setSelection(selctn?.toString());
  };

  const highlightSelectedText = useCallback(() => {
    const options = {
      commentId: '0',
      commentType: INTERMEDIATE_COMMENT,
    };

    state.editor?.execute('insertCommentCommand', options);
  }, [state.editor]);

  const renderFormCallback = useCallback(
    (selctn = '') => {
      setNewCommentBaseContent(selctn);
      setThreadId('');
      setDiscussionFormVisibility(true);
      updateActiveTab(documentStoreDispatch, TabNames.CommentsTab);
      forceUpdate();
      highlightSelectedText();
    },
    [documentStoreDispatch, highlightSelectedText, forceUpdate],
  );

  const updateContentIdMapCallback = useCallback(
    (map: ContentCommentIdMap) => {
      setEditorCommentMap(map);
      forceUpdate();
    },
    [forceUpdate],
  );

  useEffect(() => {
    CommentService.initSubscriber(
      onCommentClickCallback,
      () => renderFormCallback(selection),
      updateContentIdMapCallback,
    );
  }, [
    selection,
    state.editor,
    onCommentClickCallback,
    renderFormCallback,
    updateContentIdMapCallback,
  ]);

  const onTaskClickCallback = useCallback(
    (elementId) => {
      const id = elementId.split('-')[2];

      dispatch(updateActiveTask(parseInt(id, 10)));
      updateActiveTab(documentStoreDispatch, TabNames.Tasks);
      const options = {
        taskId: `rl-task-${id}`,
      };

      state.editor?.execute('activeTaskCommand', options);
    },
    [dispatch, documentStoreDispatch, state.editor],
  );

  const onTaskFormClose = () => {
    const options = {
      commentId: '0',
    };

    // We're using intermediate comment style for intermediate task also.
    state.editor?.execute('removeCommentCommand', options);
    setTaskForm(false);
  };
  const renderTaskFormCallback = useCallback(
    (positions) => {
      setTaskFormPosition(positions);
      dispatch(updateActiveTask(null));
      setTaskForm(true);
      highlightSelectedText();
    },
    [dispatch, highlightSelectedText],
  );

  useEffect(() => {
    TaskService.initSubscriber(
      onTaskClickCallback,
      (positions: ToolbarPositions) => renderTaskFormCallback(positions),
    );
  }, [selection, state.editor, onTaskClickCallback, renderTaskFormCallback]);

  const handleClick = useCallback(
    (e: any) => {
      if (e.target.dataset.rocketlaneMentionType) {
        // Let the MentionService open the Drawer
        return;
      }
      setBalloonToolbarDetails(documentStoreDispatch, {
        type: PopupType.LinkForm,
        position: {
          x: e.x,
          y: e.y,
        },
      });
      setLinkFormData({
        showEditForm: true,
        href: e.target?.tagName.toLowerCase() === 'a' ? e.target?.href : '',
      });
    },
    [documentStoreDispatch],
  );

  const handleEveryClick = useCallback(
    (e: any) => {
      setBalloonToolbarDetails(documentStoreDispatch, {
        type: PopupType.BalloonToolbar,
        position: {
          x: e.x,
          y: e.y,
        },
      });
      setLinkFormData({
        showEditForm: false,
        href: '',
      });
    },
    [documentStoreDispatch],
  );

  useTextSelection(docRef, handleSelection);
  // On every anchor click, show the hyperlink
  useContentClick(docRef, 'a', handleClick, handleEveryClick, state.editor);

  const _disableEditorForPreview = (allEditors: EditorsObject) => {
    if (isPreview || isResponsive) {
      const publicEditor = allEditors[EditorModes.Public];
      const privateEditor = allEditors[EditorModes.Private];

      toggleReadOnlyMode(true, publicEditor);
      toggleReadOnlyMode(true, privateEditor);
    }
  };

  const onEditorReady = (allEditors: EditorsObject) => {
    _disableEditorForPreview(allEditors);

    if (allEditors[EditorModes.Public]) {
      setActiveEditor(documentStoreDispatch, allEditors[EditorModes.Public]);
    }
    saveAllEditors(documentStoreDispatch, allEditors);
  };

  useEffect(() => {
    const options = {
      commentId: '0',
      commentType: INTERMEDIATE_COMMENT,
    };

    state.editor?.execute('removeCommentCommand', options);
  }, [state.editor]);

  const onEditorFocusChange = (edtr: Editor) => {
    setActiveEditor(documentStoreDispatch, edtr);
  };

  const onTitleViewStateChange = ({ isVisible, docTitle: title }) => {
    setDocTitleVisibility(isVisible);
    setDocTitle(title);
  };

  const docClass = classNames(
    styles.document,
    {
      [styles['document--ready']]: state.editor,
    },
    {
      [styles['document-title--visible']]: isDocTitleVisible || isDocFullScreen,
    },
    {
      [styles.responsive]: isResponsive,
    },
    className,
  );

  const [freezeMode, setFreezeMode] = useState<FreezeAction>({
    frozen: false,
  });
  const freezeDocument = (val: FreezeAction) => {
    queueMacrotask(() => {
      setFreezeMode(val);
    });
  };

  const userInfo = useUser();
  const { setNewApprovalData } = useApprovalsPaneContext();

  useEffect(() => {
    setNewApprovalData(undefined);
  }, [documentId, setNewApprovalData]);

  const handleRequestApprovalClick = () => {
    if (userInfo) {
      const approvalData = {
        approvalId: 0,
        documentId,
        requestBy: userInfo.user,
        requestTo: [],
        private: false,
      };

      setNewApprovalData(approvalData);
    }
    documentTabsFreeHandler.exit();
    updateActiveTab(documentStoreDispatch, TabNames.Approvals);
  };

  const pdfExportComponent = React.useRef<PDFExport>(null);
  const onExportPdfBtnClick = async () => {
    const element = docContentWrapperRef.current?.querySelector(
      '.editor_section__public',
    ) as HTMLElement;

    setDocPdfReady(true);

    if (element) {
      element.classList.add('pdf-exporting');
      const editorEl = element.querySelector(
        '.ck-editor__editable',
      ) as HTMLElement;

      if (editorEl) {
        editorEl.removeAttribute('contenteditable');
      }
      // Hide the original title and replace with a dummy as export PDF
      // not paginating with another parent.
      const headingComponent = docContentWrapperRef.current?.querySelector(
        '.document__head',
      ) as HTMLElement;

      if (headingComponent) {
        headingComponent.style.display = 'none';
      }
      const heading = window.document?.createElement('h3') as HTMLElement;

      heading.classList.add('document_export__title');
      heading.innerHTML = docTitle;
      element.prepend(heading);

      const ckContent = docContentWrapperRef.current?.querySelector(
        `#${EditorModes.Public}`,
      ) as HTMLElement;

      if (ckContent) {
        ckContent.remove();
      }

      const images =
        docContentWrapperRef.current?.getElementsByTagName('img') ?? [];

      if (images.length > 0) {
        for (let i = 0; i < images.length; i += 1) {
          if (!images[i].src) {
            images[i].src = 'test';
          }
        }
      }

      const allPageElements = element.querySelectorAll(
        '.page-break.ck-widget',
      ) as NodeListOf<HTMLElement>;

      for (let i = 0; i < allPageElements.length; i += 1) {
        allPageElements[i].style.display = 'none';
      }

      if (pdfExportComponent.current) {
        pdfExportComponent.current.save(() => {
          element.classList.remove('pdf-exporting');
          for (let i = 0; i < allPageElements.length; i += 1) {
            allPageElements[i].style.display = 'flex';
          }
          if (editorEl) {
            editorEl.contentEditable = 'true';
          }
          if (ckContent) {
            element.prepend(ckContent);
          }
          if (headingComponent) {
            headingComponent.style.display = 'block';
          }
          element.removeChild(heading);
          setDocPdfReady(false);
        });
      }
    } else {
      setDocPdfReady(false);
    }
  };

  const renderExtraFields = () => {
    return (
      <div className={styles['extra-toolbar-actions']}>
        <div className={styles['collab-actions']}>
          {state.allEditors && !isResponsive ? (
            <CollabActions
              exportPdfloading={isReadyToExportPdf}
              onFreezeToggle={freezeDocument}
              onExportPdfBtnClick={onExportPdfBtnClick}
              onApprovalBtnClick={handleRequestApprovalClick}
              docFreeze={freezeMode}
              allEditors={state.allEditors}
              documentId={documentId}
              projectId={projectId}
            />
          ) : null}
        </div>
        {!freezeMode.frozen && documentObj ? (
          <LastUpdatedBy
            projectId={projectId}
            documentId={documentId}
            document={documentObj}
          />
        ) : null}
        <div
          className={classNames(styles.presence)}
          ref={refCollaboratorsListElement}
        />
        {renderExtraToolbarOptions ? (
          <div>{renderExtraToolbarOptions()}</div>
        ) : null}
      </div>
    );
  };

  const onDocumentLoaded = (doc: Doc) => {
    freezeDocument({
      frozen: doc.frozen,
      frozenAt: doc.frozenAt,
      frozenBy: doc.frozenBy,
    });
    setDocumentDetails(documentStoreDispatch, doc);
  };

  const onDiscussionFormClose = useCallback((val: boolean) => {
    setDiscussionFormVisibility(val);
  }, []);

  const project = useProjectDetail(ProjectId(projectId!));
  const { spaceTabId, spaceId } = useParams<UrlParamTypes>();
  const { currentSpaceDetails } = useSpaceActions({
    spaceId,
    project: project.data!,
    spaceTabId,
  });

  const distractionFreeHandler = useDistractionFreeMode();

  const isFullWidth = useMemo(() => {
    if (documentTabsFreeHandler.active && distractionFreeHandler.active) {
      return true;
    }

    return false;
  }, [distractionFreeHandler.active, documentTabsFreeHandler.active]);
  const { isAllowedForCurrentUser } = usePermissionForRule();
  const balloonToolOptions = useMemo(
    () =>
      [
        SupportedBalloonToolbarOptions.Link,
        SupportedBalloonToolbarOptions.Comment,
      ].concat(
        isAllowedForCurrentUser(PermissionRuleName.TaskCreateAction)
          ? [SupportedBalloonToolbarOptions.Task]
          : [],
      ),
    [isAllowedForCurrentUser],
  );

  return (
    <div className={docClass}>
      <PrivateSectionContext.Provider
        value={{
          isPrivateSectionEnabled: privateSectionEnabled,
          togglePrivateSection: () => {
            setPrivateSectionStatus(true);

            queueMacrotask(() => {
              if (state.allEditors) {
                state.allEditors[EditorModes.Private].editing.view.focus();
              }
            });
          },
        }}
      >
        <Topbar
          hideTools={freezeMode.frozen}
          topbarClassName={topbarClassName}
          renderExtraFields={renderExtraFields}
          editor={state.editor}
          showDocTitle={!isDocTitleVisible && !isDocFullScreen}
          docTitle={documentTitle}
          isResponsive={isResponsive}
        />
      </PrivateSectionContext.Provider>
      <DocBalloonToolbarRef ref={refBalloonPopup} />

      <CKMentionPopup
        projectId={projectId}
        mentionData={mentionsData}
        onUserMention={onUserMention}
        editor={state.editor}
        attachTo={refMentionPopup.current}
        updateParentElementLocation={(position) => {
          setMentionPopupPosition({
            left: position.left,
            top: position.top,
          });
        }}
      >
        <div
          ref={refMentionPopup}
          style={{
            top: mentionPopupPosition.top,
            left: mentionPopupPosition.left,
          }}
          className={`${styles.mention_popup}`}
        />
      </CKMentionPopup>

      <CKVariablesPopup
        projectId={projectId}
        editor={state.editor}
        attachTo={refMentionPopup.current}
        updateParentElementLocation={(position) => {
          setMentionPopupPosition({
            left: position.left,
            top: position.top,
          });
        }}
      >
        <div
          ref={refMentionPopup}
          style={{
            top: mentionPopupPosition.top,
            left: mentionPopupPosition.left,
          }}
          className={`${styles.mention_popup}`}
        />
      </CKVariablesPopup>

      <DocumentTaskPopup
        docTitle={docTitle}
        projectId={projectId}
        docRefText={selection || ''}
        docId={documentId}
        showTaskForm={showTaskForm}
        onTaskCreated={onTaskFormClose}
        onClose={onTaskFormClose}
      >
        <div
          style={{ top: taskFormPosition.top, left: taskFormPosition.left }}
          className={styles.task_popup}
        />
      </DocumentTaskPopup>
      <Popover
        overlayClassName={`${styles.overlay_newlink}`}
        visible={state.balloonToolbarDetails.type === PopupType.LinkForm}
        content={
          <LinkForm
            edit={showLinkFormData.showEditForm}
            href={showLinkFormData.href}
          />
        }
      >
        <div
          style={{
            top: state.balloonToolbarDetails.position.y,
            left: state.balloonToolbarDetails.position.x,
          }}
          className={styles.task_popup}
        />
      </Popover>
      {state.editor && !isResponsive ? (
        <TextSelectionPopover
          selectionRef={docRef}
          scrollRef={refDocumentWrapper}
          containerNode={refBalloonPopup.current}
        >
          <DocBalloonToolbar
            balloonToolOptions={balloonToolOptions}
            editor={state.editor}
          />
        </TextSelectionPopover>
      ) : null}
      <div
        className={classNames({
          [styles['editor-pdf-ready']]: isReadyToExportPdf,
        })}
      />
      <style>{fontStyles}</style>
      <div
        className={classNames(styles.wrapper, wrapperClassName, {
          [styles.wrapper_show_pane]: state.editor,
        })}
      >
        <div className={`editor__wrapper ${styles.editor__wrapper}`}>
          <DistractionFreeModeBtnStyled isResponsive={isResponsive} />
          <div
            className={classNames(styles.editor, editorClassName)}
            ref={refDocumentWrapper}
          >
            <DocumentHeaderWrapper
              maxWidth={isFullWidth}
              isResponsive={isResponsive}
            >
              <HeaderWrapper>
                <SpaceNameStyle>
                  {currentSpaceDetails?.spaceName} /
                </SpaceNameStyle>
              </HeaderWrapper>
            </DocumentHeaderWrapper>
            <ErrorBoundary fallback={renderFallback}>
              <div
                ref={docRef}
                className={classNames({
                  [styles.doc__frozen]: freezeMode.frozen,
                })}
              >
                <DocumentEditor
                  singleUserMode={!!isPreview || isResponsive}
                  onDocumentTitleChange={onTitleChange}
                  docId={documentId}
                  projectId={projectId}
                  readOnly={!!isResponsive}
                  isResponsive={isResponsive}
                  refDocContentWrapper={docContentWrapperRef}
                  refDocumentWrapper={refDocumentWrapper}
                  refCollaboratorsListElement={refCollaboratorsListElement}
                  onTitleViewStateChange={onTitleViewStateChange}
                  onEditorReady={onEditorReady}
                  onDocumentLoaded={onDocumentLoaded}
                  distractionFreeMode={distractionFreeHandler.active}
                  documentTabsFreeMode={documentTabsFreeHandler.active}
                  onEditorFocusChange={onEditorFocusChange}
                  isPrivateSectionEnabled={privateSectionEnabled}
                  emitPrivateSectionStatus={(val) => {
                    setPrivateSectionStatus(val);
                  }}
                  maxWidth={isFullWidth}
                  pdfExportComponent={pdfExportComponent}
                />
              </div>
            </ErrorBoundary>
          </div>
        </div>
        {isResponsive === true ? null : (
          <MemoizedDocTabs
            projectId={projectId}
            documentId={documentId}
            docTitle={docTitle}
            isPrivate={isPrivate}
            canShowComments={canShowComments}
            commentThreadId={commentThreadId}
            editorCommentMap={editorCommentMap}
            newCommentBaseContent={newCommentBaseContent}
            showDiscussionForm={showDiscussionForm}
            onDiscussionFormClose={onDiscussionFormClose}
          />
        )}
      </div>
    </div>
  );
};
